import { formatMoney } from 'theme';

class SubscriptionWidget extends HTMLElement {
  constructor() {
    super();

    this.cachedPrices = {
      current: null,
      compareAt: null,
    };

    this.dropdown = this.querySelector('.subscription-dropdown');
    this.dropdown &&
      this.dropdown.addEventListener('subscription-option-selected', this.onDropdownOptionSelected.bind(this));

    this.priceElements = {
      container: this.querySelector('.subscription__subs .subscription-label-right'),
      current: this.querySelector('.subscription__subs .subscription-label-price'),
      compareAt: this.querySelector('.subscription__subs .subscription-label-compare-at-price'),
      saveAmount: this.querySelector('.subscription__subs .subscription-label-save-amount'),
    };

    const productInfoPrice = document.querySelector('.product-info__price');
    this.mainPriceElements = productInfoPrice
      ? {
          container: productInfoPrice.querySelector('price-list'),
          current: productInfoPrice.querySelector('sale-price'),
          compareAt: productInfoPrice.querySelector('compare-at-price'),
        }
      : null;

    this.priceJson = document.getElementById('PricesData');
    this.parsePriceJson();

    this.oneTimePurchaseOption = this.querySelector('.subscription__one-time');
    this.oneTimePurchaseOptionInput = this.oneTimePurchaseOption?.querySelector('input');
    this.subscriptionOption = this.querySelector('.subscription__subs');
    this.subscriptionOptionInput = this.subscriptionOption?.querySelector('input');

    if (this.oneTimePurchaseOptionInput && this.subscriptionOptionInput) {
      this.handlePurchaseOptionChange = this.handlePurchaseOptionChange.bind(this);
      this.oneTimePurchaseOptionInput.addEventListener('change', this.handlePurchaseOptionChange);
      this.subscriptionOptionInput.addEventListener('change', this.handlePurchaseOptionChange);
    }
  }

  connectedCallback() {
    // The initial price sync (buy-button total, price block) normally rides on the
    // dropdown's own initial selection. Nothing else emits it, so when the markup has
    // no selectable option -- e.g. a single-plan selector rendered as a static label --
    // run it here from the plan the form already posts. Guarded so the dropdown path
    // stays the single emitter whenever it can fire.
    if (this._initialSyncDone) return;
    if (this.querySelector('.subscription-dropdown-option.selected')) return;

    const planInput = this.querySelector(this.sellingPlanInputSelector);
    if (!planInput || planInput.value === '') return;

    this._initialSyncDone = true;
    this.updateSellingPlan(planInput.value);
  }

  get formButton() {
    if (!this._formButton) {
      this._formButton =
        this._formButton ||
        (this.sellingPlanInput?.getAttribute('form')
          ? document.getElementById(this.sellingPlanInput?.getAttribute('form'))
          : this.closest('product-info').querySelector('product-info__buy-buttons buy-buttons [type="submit"]')
        )?.querySelector('button') ||
        null;
    }

    return this._formButton;
  }

  get updateUrl() {
    return true;
  }

  get sellingPlanInputSelector() {
    return 'input[name="selling_plan"]';
  }

  get sellingPlanInput() {
    if (!this._sellingPlanInput) {
      this._sellingPlanInput = this.querySelector(this.sellingPlanInputSelector);
      this.cachedSellingPlanId = this._sellingPlanInput.value;
    }
    return this._sellingPlanInput;
  }

  parsePriceJson() {
    try {
      this.priceData = JSON.parse(this.priceJson.textContent);
    } catch (error) {
      console.error('Failed to parse price JSON:', error);
      this.priceData = null;
    }
  }

  onDropdownOptionSelected({ detail }) {
    const sellingPlanId = detail.sellingPlanId;
    if (!sellingPlanId) return;

    this.updateSellingPlan(sellingPlanId);
  }

  updateSellingPlan(sellingPlanId) {
    this.sellingPlanInput.value = sellingPlanId;
    this.updatePriceElements(sellingPlanId);
    this.dispatchSellingPlanEvent(sellingPlanId);

    if (!this.updateUrl) return;

    const searchParams = new URLSearchParams(window.location.search);
    sellingPlanId ? searchParams.set('selling_plan', sellingPlanId) : searchParams.delete('selling_plan');
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${searchParams ? '?' + searchParams.toString() : ''}`
    );
  }

  updatePriceElements(sellingPlanId) {
    if (!this.priceData) return;

    const result = this.getPrices(sellingPlanId);
    if (!result) {
      this.resetMainPriceBlock();
      return;
    }

    const {
      prices: { current, compareAt },
      changes,
    } = result;

    // A plan whose compare-at is absent (0) or equal to its price is not on sale.
    // Comparing with `!==` marked both as sales and painted a "€0,00" struck-through
    // price. Same test as bundle-offers-selector.js's _updateSubsLabel().
    const rowHasSale = compareAt > current;

    if (this.priceElements.compareAt)
      this.priceElements.compareAt.textContent = rowHasSale ? formatMoney(compareAt) : '';
    if (changes.includes('current') && this.priceElements.current)
      this.priceElements.current.textContent = formatMoney(current);

    if (this.priceElements.container) {
      this.priceElements.container.classList.toggle('has-sale', rowHasSale);
    }

    if (this.priceElements.saveAmount) {
      this.priceElements.saveAmount.textContent = `(${window.themeVariables.strings.saveBadge.replace(
        '[savings]',
        formatMoney(compareAt - current)
      )})`;
      this.priceElements.saveAmount.classList.toggle('hidden', !rowHasSale);
    }

    this.updateMainPriceBlock(current, compareAt, changes);
  }

  updateMainPriceBlock(current, compareAt, changes) {
    if (!this.mainPriceElements) return;

    if (changes.includes('current') && this.mainPriceElements.current) {
      this.mainPriceElements.current.textContent = formatMoney(current);
    }

    // `selling_plan_allocation.compare_at_price` is the variant price before the
    // plan's own adjustment, so a plan that discounts nothing (e.g. the EVO +
    // 12-month bundle, 0%) reports compare-at === price. The variant's
    // `compare_at_price` is a separate, merchant-set sale price and must survive
    // this sync -- floor the plan's compare-at with it, or the PDP drops a real
    // compare-at on load. resetMainPriceBlock() already reads the same value.
    const baseCompareAt = this.priceData.baseProductPrices.compareAt || 0;
    const effectiveCompareAt = Math.max(compareAt, baseCompareAt);
    const hasSale = effectiveCompareAt > current;

    if (this.mainPriceElements.current) {
      this.mainPriceElements.current.className = hasSale ? 'text-lg text-on-sale' : 'text-lg';
    }

    if (hasSale) {
      if (this.mainPriceElements.compareAt) {
        if (changes.includes('compareAt')) {
          this.mainPriceElements.compareAt.textContent = formatMoney(effectiveCompareAt);
        }
      } else {
        const compareAtEl = document.createElement('compare-at-price');
        compareAtEl.className = 'text-subdued line-through';
        compareAtEl.textContent = formatMoney(effectiveCompareAt);
        this.mainPriceElements.current.after(compareAtEl);
        this.mainPriceElements.compareAt = compareAtEl;
      }
    } else if (this.mainPriceElements.compareAt) {
      this.mainPriceElements.compareAt.remove();
      this.mainPriceElements.compareAt = null;
    }
  }

  resetMainPriceBlock() {
    if (!this.mainPriceElements || !this.priceData) return;

    const baseCurrent = this.priceData.baseProductPrices.current;
    const baseCompareAt = this.priceData.baseProductPrices.compareAt;
    const hasSale = baseCompareAt > 0 && baseCompareAt > baseCurrent;

    if (this.mainPriceElements.current) {
      this.mainPriceElements.current.textContent = formatMoney(baseCurrent);
      this.mainPriceElements.current.className = hasSale ? 'text-lg text-on-sale' : 'text-lg';
    }

    if (hasSale) {
      if (this.mainPriceElements.compareAt) {
        this.mainPriceElements.compareAt.textContent = formatMoney(baseCompareAt);
      } else {
        const compareAtEl = document.createElement('compare-at-price');
        compareAtEl.className = 'text-subdued line-through';
        compareAtEl.textContent = formatMoney(baseCompareAt);
        this.mainPriceElements.current.after(compareAtEl);
        this.mainPriceElements.compareAt = compareAtEl;
      }
    } else if (this.mainPriceElements.compareAt) {
      this.mainPriceElements.compareAt.remove();
      this.mainPriceElements.compareAt = null;
    }

    this.cachedPrices = { current: null, compareAt: null };
  }

  getPrices(sellingPlanId) {
    const sellingPlan = this.priceData.sellingPlanAllocations.find((plan) => plan.sellingPlanId == sellingPlanId);
    if (!sellingPlan) return;

    const changes = [];

    const newCurrent = sellingPlan.price;
    const newCompareAt = sellingPlan.compareAtPrice;

    if (this.cachedPrices.current !== newCurrent) {
      this.cachedPrices.current = newCurrent;
      changes.push('current');
    }
    if (this.cachedPrices.compareAt !== newCompareAt) {
      this.cachedPrices.compareAt = newCompareAt;
      changes.push('compareAt');
    }

    return { prices: this.cachedPrices, changes };
  }

  handlePurchaseOptionChange(event) {
    const selectedInput = event.target;

    if (selectedInput.value === 'one-time') {
      this.sellingPlanInput.value = '';
      this.resetMainPriceBlock();
      this.dispatchSellingPlanEvent(null);
    } else {
      const sellingPlanId = this.cachedSellingPlanId;
      if (!sellingPlanId) return;
      this.updateSellingPlan(sellingPlanId);
    }
  }

  dispatchSellingPlanEvent(sellingPlanId) {
    const event = new CustomEvent('selling-plan-changed', {
      detail: { sellingPlanId },
    });
    document.dispatchEvent(event);
  }

  disconnectedCallback() {
    this.dropdown &&
      this.dropdown.removeEventListener('subscription-option-selected', this.onDropdownOptionSelected.bind(this));
    this.oneTimePurchaseOptionInput &&
      this.oneTimePurchaseOptionInput.removeEventListener('change', this.handlePurchaseOptionChange);
    this.subscriptionOptionInput &&
      this.subscriptionOptionInput.removeEventListener('change', this.handlePurchaseOptionChange);
  }
}
class BundleSubscriptionWidget extends SubscriptionWidget {
  constructor() {
    super();

    this.isPlanApplied = this.sellingPlanInput.value !== '';

    this.updateUniqueBundleId = this.updateUniqueBundleId.bind(this);
    document.addEventListener('variant:add', this.updateUniqueBundleId);

    // Get input fields
    this.subscriptionInputs = {
      id: this.querySelector('input[name="items[1][id]"]'),
      sellingPlan: this.querySelector('input[name="items[1][selling_plan]"]'),
      baseProductBundleId: this.querySelector('input[name="items[0][properties][_bundleId]"]'),
      bundleProductBundleId: this.querySelector('input[name="items[1][properties][_bundleId]"]'),
    };
  }

  get updateUrl() {
    return false;
  }

  get sellingPlanInputSelector() {
    return 'input[name="items[1][selling_plan]"]';
  }

  onDropdownOptionSelected({ detail }) {
    const sellingPlanId = detail.sellingPlanId;
    this.updateSellingPlan(sellingPlanId);
    this.cachedSellingPlanId = sellingPlanId;

    this.subscriptionOptionInput.checked = true;
    this.isPlanApplied = true;
  }

  handlePurchaseOptionChange(event) {
    const selectedInput = event.target;

    if (selectedInput.value === 'one-time') {
      this.isPlanApplied = false;
      this.sellingPlanInput.value = '';
      this.resetMainPriceBlock();
      this.dispatchSellingPlanEvent(null);
    } else {
      const sellingPlanId = this.cachedSellingPlanId;
      if (!sellingPlanId) return;
      this.isPlanApplied = true;
      this.updateSellingPlan(sellingPlanId);
    }

    this.toggleSubscriptionFields();
  }

  toggleSubscriptionFields() {
    Object.values(this.subscriptionInputs).forEach((input) => {
      if (input && input instanceof HTMLInputElement) {
        input.disabled = !this.isPlanApplied;
      }
    });
  }

  getPrices(sellingPlanId) {
    const changes = [];

    const sellingPlan = this.priceData.sellingPlanAllocations.find((plan) => plan.sellingPlanId == sellingPlanId);

    if (sellingPlan) {
      const baseCompareAtPrice = Math.max(
        this.priceData.baseProductPrices.compareAt,
        this.priceData.baseProductPrices.current
      );

      const newCurrentPrice = sellingPlan.price + this.priceData.baseProductPrices.current;
      const newCompareAtPrice = sellingPlan.compareAtPrice + baseCompareAtPrice;

      if (this.cachedPrices.current !== newCurrentPrice) {
        this.cachedPrices.current = newCurrentPrice;
        changes.push('current');
      }
      if (this.cachedPrices.compareAt !== newCompareAtPrice) {
        this.cachedPrices.compareAt = newCompareAtPrice;
        changes.push('compareAt');
      }
    }

    return { prices: this.cachedPrices, changes };
  }

  updateUniqueBundleId() {
    // Since this id is resolved once, if a customer doesn't leave the PDP when adding a bundle to the cart,
    // the id will be the same for any consecutive bundle added to the cart
    if (!this.subscriptionInputs.baseProductBundleId || !this.subscriptionInputs.bundleProductBundleId) return;
    const newUnique = Date.now() * 1000 + Math.floor(Math.random() * 1000);
    this.subscriptionInputs.baseProductBundleId.value = newUnique;
    this.subscriptionInputs.bundleProductBundleId.value = newUnique;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('variant:add', this.updateUniqueBundleId);
  }
}

// The single-plan subscription card (snippets/subscription-clarity.liquid): a widget
// with nothing to select -- no dropdown, no one-time option, just the hidden
// selling_plan input the form posts. That is the case connectedCallback's initial-sync
// path already describes ("markup has no selectable option ... run it here from the
// plan the form already posts"), so the buy-button total, the price block and the
// ?selling_plan= param are all synced by the same code every other subscription
// surface on the PDP runs. Nothing to add: the widget-only elements the constructor
// looks for (dropdown, .subscription__subs prices, one-time input) are absent here and
// already null-guarded, which leaves exactly that initial sync.
class SubscriptionClarity extends SubscriptionWidget {}

customElements.define('subscription-widget', SubscriptionWidget);
customElements.define('bundle-subscription-widget', BundleSubscriptionWidget);
customElements.define('subscription-clarity', SubscriptionClarity);

class SubscriptionDropdown extends HTMLElement {
  constructor() {
    super();

    this.isDropdownOpen = false;

    this.dropdownTrigger = this.querySelector('.subscription-dropdown-trigger');
    this.dropdownOptionsWrapper = this.querySelector('.sbuscription-dropdown-options-wrapper');
    this.dropdownOptions = this.querySelector('.subscription-dropdown-options');
    this.dropdownOptionItems = this.dropdownOptions?.querySelectorAll('.subscription-dropdown-option') || [];

    this.collapsible = this.closest('.subscription-collapsible');

    this.mql = window.matchMedia('(min-width: 750px)');

    this.toggleDropdown = this.toggleDropdown.bind(this);
    this.handleDropdownTriggerKeydown = this.handleDropdownTriggerKeydown.bind(this);
    this.handleOptionClick = this.handleOptionClick.bind(this);
    this.handleOptionKeydown = this.handleOptionKeydown.bind(this);
    this.handleOutsideClick = this.handleOutsideClick.bind(this);

    this.mql.addEventListener('change', this.setFullHeight.bind(this));
  }

  connectedCallback() {
    this.setFullHeight();
    this.initDropdown();
    this.initSelectedOption();
  }

  initSelectedOption() {
    const initialSellingPlanId = this.dropdownOptions?.querySelector('.subscription-dropdown-option.selected')?.dataset
      ?.sellingPlanId;
    if (!initialSellingPlanId) return;
    this.dispatchOptionSelectedEvent(initialSellingPlanId);
  }

  initDropdown() {
    // The single-plan markup renders a static label with no trigger (see
    // snippets/subscription-dropdown.liquid). Bail before wiring rather than throwing,
    // so connectedCallback still reaches initSelectedOption() and emits the initial
    // `selling-plan-changed`.
    if (!this.dropdownTrigger || !this.dropdownOptions) return;

    this.dropdownTrigger.addEventListener('click', this.toggleDropdown);
    this.dropdownTrigger.addEventListener('keydown', this.handleDropdownTriggerKeydown);
    this.dropdownOptions.addEventListener('click', this.handleOptionClick);
    this.dropdownOptions.addEventListener('keydown', this.handleOptionKeydown);

    document.addEventListener('click', this.handleOutsideClick);
  }

  setFullHeight() {
    this.fullHeight = this.dropdownOptions?.offsetHeight || 0;
  }

  handleDropdownTriggerKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      this.toggleDropdown(event);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.closeDropdown();
    }
  }

  toggleDropdown(event) {
    event.preventDefault();
    event.stopPropagation();

    this.isDropdownOpen ? this.closeDropdown() : this.openDropdown();
  }

  closeDropdown() {
    if (!this.isDropdownOpen) return;

    this.isDropdownOpen = false;
    this.dropdownOptionsWrapper.style.height = '0';
    this.classList.remove('active');
    this.dropdownTrigger.setAttribute('aria-expanded', 'false');
    this.dropdownOptionItems.forEach((option) => {
      option.setAttribute('tabindex', '-1');
    });
  }

  openDropdown() {
    this.isDropdownOpen = true;
    this.dropdownOptionsWrapper.style.height = `${this.fullHeight}px`;
    this.classList.add('active');
    this.dropdownTrigger.setAttribute('aria-expanded', 'true');
    this.dropdownOptionItems.forEach((option) => {
      if (option.classList.contains('selected')) return;
      option.setAttribute('tabindex', '0');
    });
  }

  handleOptionClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const target = event.target.classList.contains('subscription-dropdown-option')
      ? event.target
      : event.target.closest('.subscription-dropdown-option');

    if (!target || target.classList.contains('selected')) return;

    const sellingPlanId = target.dataset.sellingPlanId;
    const text = target.querySelector('.subscription-dropdown-option-text')?.textContent.trim() || null;

    if (!sellingPlanId) return;
    text != null && this.updateDropdownTriggerText(text);
    this.updateSelectedOption(sellingPlanId, target);

    this.dispatchOptionSelectedEvent(sellingPlanId);

    if (event.type === 'keydown') {
      this.dropdownTrigger.focus();
    }
    this.closeDropdown();
  }

  handleOptionKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      this.handleOptionClick(event);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();

      this.dropdownTrigger.focus();
      this.closeDropdown();
    }
  }

  handleOutsideClick(event) {
    if (this.contains(event.target) || !this.isDropdownOpen) return;
    this.closeDropdown();
  }

  updateDropdownTriggerText(text) {
    if (this.dropdownTrigger) {
      const triggerText = this.dropdownTrigger.querySelector('.subscription-dropdown-trigger-text');
      if (triggerText) {
        triggerText.textContent = text;
      }
    }
  }

  updateSelectedOption(sellingPlanId, newSelectedOption = null) {
    if (!this.dropdownOptionItems.length) return;
    this.dropdownOptionItems.forEach((option) => {
      option.classList.remove('selected');
      option.setAttribute('aria-selected', 'false');
    });

    if (newSelectedOption) {
      newSelectedOption.classList.add('selected');
      newSelectedOption.setAttribute('aria-selected', 'true');
    } else {
      const selectedOption = this.dropdownOptions.querySelector(`[data-selling-plan-id="${sellingPlanId}"]`);
      if (!selectedOption) return;
      selectedOption.classList.add('selected');
      selectedOption.setAttribute('aria-selected', 'true');
    }
  }

  onCollapsibleToggled(event) {
    const detail = event.detail;
    this.dropdownTrigger.setAttribute('tabindex', detail.open ? '0' : '-1');
  }

  dispatchOptionSelectedEvent(sellingPlanId) {
    const event = new CustomEvent('subscription-option-selected', {
      detail: {
        sellingPlanId,
      },
      bubbles: true,
    });
    this.dispatchEvent(event);
  }

  disconnectedCallback() {
    this.dropdownTrigger && this.dropdownTrigger.removeEventListener('click', this.toggleDropdown);
    this.dropdownOptions && this.dropdownOptions.removeEventListener('click', this.handleOptionClick);
    this.mql && this.mql.removeEventListener('change', this.setFullHeight.bind(this));
    document.removeEventListener('click', this.handleOutsideClick);
  }
}

customElements.define('subscription-dropdown', SubscriptionDropdown);
